/**
 * Dynamic Limits Service
 * Handles fetching and updating plan limits from API
 */

import { updatePlanLimits, resetPlanLimits } from 'configs/LimitsConfig';

import { SEC_AUTH_M_TK } from 'constants/AuthConstant';
import { env } from 'configs/EnvironmentConfig';
import API from './API';

/**
 * Fetch plan limits from the backend API (ENABLED - fetches from /plan_limit)
 * @returns {Promise<Object>} Plan limits configuration
 */
export const fetchPlanLimitsFromAPI = async () => {
  try {
    const token = localStorage.getItem(SEC_AUTH_M_TK);
    const response = await API.get('/plan_limit', {
      headers: {
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        'ngrok-skip-browser-warning': 'true',
      },
    });
    if (response && response.data) {
      // Transform the response data to remove "PER_TENANT" from field names
      return transformPlanLimitsResponse(response.data);
    }
    throw new Error('No data in /plan_limit response');
  } catch (error) {
    console.error('Error fetching plan limits from API, falling back to static config:', error);
    resetPlanLimits();
    return getCurrentPlanLimits();
  }
};

/**
 * Transform plan limits response to remove "PER_TENANT" from field names
 * @param {Object} data - Raw API response data
 * @returns {Object} Transformed data with simplified field names
 */
const transformPlanLimitsResponse = (data) => {
  if (!data || typeof data !== 'object') {
    return data;
  }

  const transformedData = {};
  
  Object.keys(data).forEach(planKey => {
    if (typeof data[planKey] === 'object' && data[planKey] !== null) {
      transformedData[planKey] = {};
      
      Object.keys(data[planKey]).forEach(limitKey => {
        // Transform field names by removing "_PER_TENANT" suffix
        let newKey = limitKey;
        if (limitKey.endsWith('_PER_TENANT')) {
          newKey = limitKey.replace('_PER_TENANT', '');
        }
        transformedData[planKey][newKey] = data[planKey][limitKey];
      });
    } else {
      transformedData[planKey] = data[planKey];
    }
  });
  
  console.log('🔄 Transformed plan limits response:', {
    original: data,
    transformed: transformedData
  });
  
  return transformedData;
};

/**
 * Initialize limits service - using static config (no API calls)
 */
export const initializeLimitsService = async () => {
  // DISABLED: No API calls, using static configuration
  console.log('Limits service initialized with static configuration');
  resetPlanLimits(); // Ensure we're using the static limits
  return Promise.resolve();
};

/**
 * Set up periodic refresh of plan limits (DISABLED - using static config)
 * @param {number} intervalMinutes - Refresh interval in minutes (default: 30)
 */
export const setupLimitsRefresh = (intervalMinutes = 30) => {
  // DISABLED: No periodic refresh needed for static configuration
  console.log('Plan limits refresh disabled (using static configuration)');

  // Return no-op cleanup function
  return () => {
    console.log('No refresh to stop (static configuration)');
  };
};

/**
 * Manually refresh plan limits (DISABLED - using static config)
 */
export const refreshPlanLimits = async () => {
  // DISABLED: Using static configuration
  console.log('Manual refresh disabled (using static configuration)');
  return { success: true, message: 'Using static plan limits configuration' };
};

/**
 * Update plan limits for a specific subscription type (admin function)
 * @param {string} subscriptionType - The subscription type to update
 * @param {Object} limits - The new limits for this subscription type
 */
export const updateSubscriptionLimits = async (subscriptionType, limits) => {
  try {
    
    const token = localStorage.getItem(SEC_AUTH_M_TK);

    if (!token) {
      throw new Error('No authentication token available');
    }

    const response = await fetch(`${env.API_ENDPOINT_URL}/plan-limits/${subscriptionType}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'ngrok-skip-browser-warning': 'true'
      },
      body: JSON.stringify(limits)
    });

    if (!response.ok) {
      throw new Error(`Failed to update plan limits: ${response.status}`);
    }

    const updatedLimits = await response.json();

    // Refresh all limits after update
    await fetchPlanLimitsFromAPI();

    return { success: true, data: updatedLimits };

  } catch (error) {
    console.error('Error updating subscription limits:', error);
    return { success: false, message: error.message };
  }
};

/**
 * Get current plan limits (static configuration)
 * @returns {Object} Current plan limits configuration
 */
export const getCurrentPlanLimits = () => {
  // Import the limits from LimitsConfig to avoid circular dependency
  const { getPlanLimits } = require('../configs/LimitsConfig');

  // Return all plan limits
  return {
    Free: getPlanLimits('Free'),
    Starter: getPlanLimits('Starter'),
    Professional: getPlanLimits('Professional'),
    Enterprise: getPlanLimits('Enterprise')
  };
};

/**
 * Listen for plan limits updates
 * @param {Function} callback - Callback function to execute when limits are updated
 * @returns {Function} Cleanup function to remove the listener
 */
export const onPlanLimitsUpdate = (callback) => {
  const handleUpdate = (event) => {
    callback(event.detail.limits);
  };

  window.addEventListener('planLimitsUpdated', handleUpdate);

  // Return cleanup function
  return () => {
    window.removeEventListener('planLimitsUpdated', handleUpdate);
  };
};

/**
 * Validate plan limits structure
 * @param {Object} limits - Plan limits to validate
 * @returns {Object} Validation result
 */
export const validatePlanLimits = (limits) => {
  const requiredFields = [
    'MAX_CALLS_PER_USER',
    'MAX_CALL_DURATION_SECONDS',
    'MAX_AGENT_CREATION_LIMIT',
    'MAX_AGENT_CHAT_LIMIT',
    'MAX_PLAYGROUND_CHAT_LIMIT',
    'MAX_QUESTIONS_FOR_AGENT_CHAT',
    'MAX_QUESTIONS_FOR_PLAYGROUND_CHAT',
    'MAX_DOCUMENTS_UPLOAD_LIMIT',
    'MAX_AGENT_UPDATE_LIMIT'
  ];

  const requiredPlans = ['Free', 'Starter', 'Professional', 'Enterprise'];

  const errors = [];

  // Check if all required plans exist
  for (const plan of requiredPlans) {
    if (!limits[plan]) {
      errors.push(`Missing plan: ${plan}`);
      continue;
    }

    // Check if all required fields exist for each plan
    for (const field of requiredFields) {
      if (typeof limits[plan][field] !== 'number') {
        errors.push(`Missing or invalid field ${field} in plan ${plan}`);
      }
    }
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};

// Note: React hook moved to hooks/useDynamicLimits.js to avoid circular dependencies

// Export the service as default
export default {
  fetchPlanLimitsFromAPI,
  initializeLimitsService,
  setupLimitsRefresh,
  refreshPlanLimits,
  updateSubscriptionLimits,
  getCurrentPlanLimits,
  onPlanLimitsUpdate,
  validatePlanLimits
};
